/**
 * =============================================================================
 * Hook + Context: useExtranetAuth (Fase 4 - Extranet KB)
 * Versão: 1.0.0
 *
 * Autenticação isolada para usuários externos da Base de Conhecimento.
 * Espelha o padrão do módulo Afiliado (useAffiliateAuth):
 *   - Sessão Supabase própria via signInWithPassword.
 *   - Validação pós-login via edge function kb-extranet-login.
 *   - Heartbeat de presença (60s) enquanto a aba está visível.
 *   - Todo o portal é escopado por :tenantSlug da URL.
 * =============================================================================
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const log = createLogger('ExtranetAuth');

// -----------------------------------------------------------------------------
// Tipos
// -----------------------------------------------------------------------------
export interface ExtranetTenant {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
}

export interface ExtranetExternalUser {
  id: string;
  display_name: string | null;
  email: string | null;
  organization: string | null;
  is_active: boolean;
}

interface ExtranetAuthContextValue {
  session: Session | null;
  user: User | null;
  externalUser: ExtranetExternalUser | null;
  tenant: ExtranetTenant | null;
  tenantSlug: string;
  loading: boolean;
  ready: boolean;
  signUp: (input: SignUpInput) => Promise<AuthResult>;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

export interface SignUpInput {
  email: string;
  password: string;
  display_name: string;
  organization?: string;
}

export interface AuthResult {
  success: boolean;
  can_access?: boolean;
  redirect_to?: string;
  message?: string;
  error?: string;
  /** Código Supabase Auth (ex: `invalid_credentials`, `email_not_confirmed`). */
  code?: string;
  /** Código estruturado da edge (ex: `no_external_user`, `inactive`). */
  backendCode?: string;
  /** HTTP status quando disponível (útil para 429). */
  status?: number;
}

const ExtranetAuthContext = createContext<ExtranetAuthContextValue | undefined>(undefined);

// -----------------------------------------------------------------------------
// Provider
// -----------------------------------------------------------------------------
export function ExtranetAuthProvider({
  tenantSlug,
  children,
}: {
  tenantSlug: string;
  children: ReactNode;
}) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [externalUser, setExternalUser] = useState<ExtranetExternalUser | null>(null);
  const [tenant, setTenant] = useState<ExtranetTenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  const fetchingRef = useRef(false);

  // ---------------------------------------------------------------------------
  // Carrega dados do usuário externo via RPC kb_extranet_get_me
  // ---------------------------------------------------------------------------
  const loadMe = useCallback(async () => {
    if (!tenantSlug) return;
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const { data, error } = await supabase.rpc('kb_extranet_get_me', {
        p_tenant_slug: tenantSlug,
      });
      if (error) {
        log.warn('kb_extranet_get_me falhou', error);
        setExternalUser(null);
        setTenant(null);
        return;
      }
      const row = Array.isArray(data) ? data[0] : null;
      if (!row) {
        setExternalUser(null);
        setTenant(null);
        return;
      }
      setExternalUser({
        id: row.external_user_id,
        display_name: row.display_name,
        email: row.email,
        organization: row.organization,
        is_active: row.is_active,
      });
      setTenant({
        id: row.tenant_id,
        name: row.tenant_name,
        slug: tenantSlug,
        logo_url: row.tenant_logo_url,
      });
    } finally {
      fetchingRef.current = false;
      setLoading(false);
      setReady(true);
    }
  }, [tenantSlug]);

  // ---------------------------------------------------------------------------
  // Listener de auth
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          setLoading(true);
          setTimeout(() => loadMe(), 0);
        } else {
          setExternalUser(null);
          setTenant(null);
          setLoading(false);
          setReady(true);
        }
      },
    );

    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      setSession(existing);
      setUser(existing?.user ?? null);
      if (existing?.user) {
        loadMe();
      } else {
        setLoading(false);
        setReady(true);
      }
    });

    return () => subscription.unsubscribe();
  }, [loadMe]);

  // ---------------------------------------------------------------------------
  // Heartbeat de presença (60s, apenas com aba visível)
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!externalUser?.id || !tenantSlug) return;
    const HEARTBEAT_MS = 60_000;

    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      void supabase.rpc('kb_extranet_touch_presence', {
        p_tenant_slug: tenantSlug,
        p_kind: 'heartbeat',
      }).then(() => undefined, () => undefined);
    };
    const id = window.setInterval(tick, HEARTBEAT_MS);
    return () => window.clearInterval(id);
  }, [externalUser?.id, tenantSlug]);

  // ---------------------------------------------------------------------------
  // Ações
  // ---------------------------------------------------------------------------
  const signUp = useCallback(async (input: SignUpInput): Promise<AuthResult> => {
    try {
      const { data, error } = await supabase.functions.invoke('kb-extranet-signup', {
        body: { ...input, tenant_slug: tenantSlug },
      });
      if (error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ctx: any = (error as any)?.context;
        let body: Partial<AuthResult> & { code?: string } | null = null;
        try {
          if (ctx && typeof ctx.json === 'function') body = await ctx.json();
        } catch { /* ignore */ }
        return {
          success: false,
          error: body?.error ?? body?.message ?? error.message,
          backendCode: body?.code,
          redirect_to: body?.redirect_to,
          message: body?.message,
        };
      }
      return data as AuthResult;
    } catch (err) {
      log.error('signUp exception', err);
      return { success: false, error: err instanceof Error ? err.message : 'Erro inesperado' };
    }
  }, [tenantSlug]);

  const signIn = useCallback(async (email: string, password: string): Promise<AuthResult> => {
    try {
      const { data: signInData, error: signInError } =
        await supabase.auth.signInWithPassword({ email, password });
      if (signInError || !signInData.user) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const anyErr = signInError as any;
        return {
          success: false,
          error: signInError?.message ?? 'Credenciais inválidas',
          code: anyErr?.code,
          status: anyErr?.status,
        };
      }
      const { data, error } = await supabase.functions.invoke('kb-extranet-login', {
        body: { tenant_slug: tenantSlug },
      });
      if (error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ctx: any = (error as any)?.context;
        let body: Partial<AuthResult> & { code?: string } | null = null;
        try {
          if (ctx && typeof ctx.json === 'function') body = await ctx.json();
        } catch { /* ignore */ }
        await supabase.auth.signOut();
        return {
          success: false,
          error: body?.error ?? body?.message ?? 'Acesso negado',
          backendCode: body?.code,
          redirect_to: body?.redirect_to,
        };
      }
      // Edge pode retornar success:true mas can_access:false (ex: inactive)
      const result = data as AuthResult & { code?: string };
      if (result && result.can_access === false) {
        return { ...result, backendCode: result.code ?? 'inactive' };
      }
      return result;
    } catch (err) {
      log.error('signIn exception', err);
      return { success: false, error: err instanceof Error ? err.message : 'Erro inesperado' };
    }
  }, [tenantSlug]);

  const signOut = useCallback(async () => {
    try {
      await supabase.rpc('kb_extranet_log_event', {
        p_tenant_slug: tenantSlug,
        p_event_type: 'logout',
        p_metadata: {},
      });
    } catch { /* ignore */ }
    await supabase.auth.signOut();
  }, [tenantSlug]);

  const refresh = useCallback(async () => {
    await loadMe();
  }, [loadMe]);

  const value: ExtranetAuthContextValue = {
    session,
    user,
    externalUser,
    tenant,
    tenantSlug,
    loading,
    ready,
    signUp,
    signIn,
    signOut,
    refresh,
  };

  return (
    <ExtranetAuthContext.Provider value={value}>
      {children}
    </ExtranetAuthContext.Provider>
  );
}

export function useExtranetAuth(): ExtranetAuthContextValue {
  const ctx = useContext(ExtranetAuthContext);
  if (!ctx) {
    throw new Error('useExtranetAuth deve ser usado dentro de ExtranetAuthProvider');
  }
  return ctx;
}
